import { useEffect, useMemo, useState } from 'react';
import { putBotProfile } from '../../api/endpoints';
import type { BotProfile } from '../../api/types';
import { ErrorNote, Note } from '../../components/ErrorNote';
import { Field } from '../../components/Field';
import { SaveBar } from '../../components/setup/SetupKit';
import { Toggle } from '../../components/Toggle';

interface ProfileDraft {
  name: string;
  agentDisplayName: string;
  systemContext: string;
  handoffMessage: string;
  resolutionMessage: string;
  replyMode: 'auto' | 'draft';
  offerHumanHandoff: boolean;
  allowRateEstimates: boolean;
  allowDataAccessLoggedIn: boolean;
  autoResolveAfterMinutes: string;
  maxRunsPerConversationDay: string;
  maxRunsPerTenantDay: string;
  guardrails: string[];
}

function draftFrom(profile: BotProfile | null): ProfileDraft {
  return {
    name: profile?.name ?? 'Assistant',
    agentDisplayName: profile?.agentDisplayName ?? '',
    systemContext: profile?.systemContext ?? '',
    handoffMessage: profile?.handoffMessage ?? '',
    resolutionMessage: profile?.resolutionMessage ?? '',
    replyMode: profile?.flags.replyMode === 'draft' ? 'draft' : 'auto',
    offerHumanHandoff: profile?.flags.offerHumanHandoff ?? false,
    allowRateEstimates: profile?.flags.allowRateEstimates ?? false,
    allowDataAccessLoggedIn: profile?.flags.allowDataAccessLoggedIn ?? false,
    autoResolveAfterMinutes:
      profile?.flags.autoResolveAfterMinutes != null
        ? String(profile.flags.autoResolveAfterMinutes)
        : '',
    maxRunsPerConversationDay:
      profile?.flags.maxRunsPerConversationDay != null
        ? String(profile.flags.maxRunsPerConversationDay)
        : '',
    maxRunsPerTenantDay:
      profile?.flags.maxRunsPerTenantDay != null ? String(profile.flags.maxRunsPerTenantDay) : '',
    guardrails: profile?.guardrails ?? [],
  };
}

const intOrUndefined = (value: string) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

/** The body a draft would PUT — module level so its identity is stable. */
const bodyOf = (draft: ProfileDraft) => ({
  name: draft.name.trim(),
  agentDisplayName: draft.agentDisplayName.trim(),
  systemContext: draft.systemContext.trim() === '' ? null : draft.systemContext,
  handoffMessage: draft.handoffMessage.trim() === '' ? null : draft.handoffMessage,
  resolutionMessage: draft.resolutionMessage.trim() === '' ? null : draft.resolutionMessage,
  flags: {
    replyMode: draft.replyMode,
    offerHumanHandoff: draft.offerHumanHandoff,
    allowRateEstimates: draft.allowRateEstimates,
    allowDataAccessLoggedIn: draft.allowDataAccessLoggedIn,
    autoResolveAfterMinutes: intOrUndefined(draft.autoResolveAfterMinutes),
    maxRunsPerConversationDay: intOrUndefined(draft.maxRunsPerConversationDay),
    maxRunsPerTenantDay: intOrUndefined(draft.maxRunsPerTenantDay),
  },
  guardrails: draft.guardrails,
});

/**
 * Who the assistant is and what it may do — everything on the profile except
 * whether it is on, which is the screen header's switch: one control for the
 * one decision an operator comes back to most, and nothing in this form can
 * turn it on by accident on a save.
 *
 * Dirty is measured on the BODY against the profile the last save answered
 * with, so a trailing space is not a change nobody can see (the analytics
 * forms' rule), and the edit survives a look at another section.
 */
export function AssistantSection({
  profile,
  onSaved,
  onDirty,
}: {
  profile: BotProfile;
  onSaved: (fresh: BotProfile) => void;
  onDirty: (section: 'assistant', dirty: boolean) => void;
}) {
  const [base, setBase] = useState<BotProfile>(profile);
  const [draft, setDraft] = useState<ProfileDraft>(() => draftFrom(profile));
  const dirty = useMemo(
    () => JSON.stringify(bodyOf(draft)) !== JSON.stringify(bodyOf(draftFrom(base))),
    [draft, base],
  );
  useEffect(() => onDirty('assistant', dirty), [onDirty, dirty]);
  useEffect(() => () => onDirty('assistant', false), [onDirty]);
  const [guardrailInput, setGuardrailInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const set = <K extends keyof ProfileDraft>(key: K, value: ProfileDraft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setSaved(false);
  };

  const addGuardrail = () => {
    const text = guardrailInput.trim();
    if (!text || draft.guardrails.length >= 20) return;
    set('guardrails', [...draft.guardrails, text]);
    setGuardrailInput('');
  };

  const save = async () => {
    if (!draft.name.trim()) {
      setError('Give the assistant a name.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const fresh = await putBotProfile(bodyOf(draft));
      setBase(fresh);
      setDraft(draftFrom(fresh));
      setSaved(true);
      onSaved(fresh);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save the assistant.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
    <section className="card card-pad manage-card">
      <h2 className="manage-card-title">Assistant</h2>
      {error && <ErrorNote error={error} />}

      <Field label="Name" hint="What visitors see above its replies in the chat widget.">
        <input className="input" value={draft.name} onChange={(e) => set('name', e.target.value)} />
      </Field>

      <div className="bot-row">
        <Field
          label="Name for your team"
          hint="One name for every human reply — never the agent's own. Blank names nobody."
        >
          <input
            className="input"
            placeholder="e.g. Support"
            value={draft.agentDisplayName}
            onChange={(e) => set('agentDisplayName', e.target.value)}
          />
        </Field>
        <Toggle
          checked={draft.offerHumanHandoff}
          onChange={(on) => set('offerHumanHandoff', on)}
          title="Let visitors ask for a person"
          sub="Shows “Talk to a person” in the chat widget; pressing it hands the conversation to your team."
        />
      </div>

      <Field
        label="Business context"
        hint="Who you are, what you offer, tone — the assistant’s standing instructions."
      >
        <textarea
          className="textarea"
          rows={5}
          value={draft.systemContext}
          onChange={(e) => set('systemContext', e.target.value)}
          placeholder="We are LimoSphere, a chauffeured transportation company offering airport transfers and hourly charters…"
        />
      </Field>

      <div className="bot-row">
        <Field label="Handoff message" hint="Sent when it hands a conversation to your team.">
          <textarea
            className="textarea"
            rows={2}
            value={draft.handoffMessage}
            onChange={(e) => set('handoffMessage', e.target.value)}
          />
        </Field>
        <Field label="Resolution message" hint="Sent when it resolves a conversation.">
          <textarea
            className="textarea"
            rows={2}
            value={draft.resolutionMessage}
            onChange={(e) => set('resolutionMessage', e.target.value)}
          />
        </Field>
      </div>

      <h3 className="bot-subhead">Guardrails</h3>
      <p className="manage-card-sub">
        Hard rules the assistant must follow, up to 20. Saved as a whole — removing one here removes
        it for real.
      </p>
      {draft.guardrails.length > 0 && (
        <ul className="bot-guardrails">
          {draft.guardrails.map((rule, index) => (
            <li key={`${index}-${rule}`}>
              <span>{rule}</span>
              <button
                className="linklike"
                onClick={() => set('guardrails', draft.guardrails.filter((_, i) => i !== index))}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="bot-guardrail-add">
        <input
          className="input"
          value={guardrailInput}
          placeholder="Never quote a price — offer a rate estimate instead."
          onChange={(e) => setGuardrailInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addGuardrail();
            }
          }}
        />
        <button
          className="btn btn-secondary"
          onClick={addGuardrail}
          disabled={!guardrailInput.trim() || draft.guardrails.length >= 20}
        >
          Add
        </button>
      </div>
      {draft.guardrails.length >= 20 && (
        <p className="field-hint">Guardrail limit reached (20).</p>
      )}

      <h3 className="bot-subhead">How it replies</h3>
      <div className="bot-mode-switch">
        {(
          [
            {
              value: 'auto' as const,
              label: 'Send replies',
              sub: 'The assistant answers the customer itself and hands off when it should.',
            },
            {
              value: 'draft' as const,
              label: 'Draft only — a person sends',
              sub: 'It writes the reply as a private note on the conversation. Nothing reaches the customer until one of your team sends it.',
            },
          ]
        ).map((option) => (
          <button
            key={option.value}
            type="button"
            className={`bot-mode ${draft.replyMode === option.value ? 'is-on' : ''}`}
            onClick={() => set('replyMode', option.value)}
          >
            <span className="bot-mode-label">{option.label}</span>
            <span className="bot-mode-sub">{option.sub}</span>
          </button>
        ))}
      </div>
      {draft.replyMode === 'draft' && (
        <Note tone="info">
          In draft mode the conversation is your team's from the start — it never sits in the bot
          queue, and the assistant keeps drafting on every new customer message, including after
          someone has replied.
        </Note>
      )}

      <h3 className="bot-subhead">Capabilities & limits</h3>
      <Toggle
        checked={draft.allowRateEstimates}
        onChange={(on) => set('allowRateEstimates', on)}
        title="Rate estimates"
        sub="May quote every vehicle type's price through the same rate engine as your reservation website."
      />
      <Toggle
        checked={draft.allowDataAccessLoggedIn}
        onChange={(on) => set('allowDataAccessLoggedIn', on)}
        title="Account data access"
        sub="May look up reservations for customers matched to a dashboard account."
      />
      <div className="bot-row bot-row-3">
        <Field label="Auto-resolve after (min)" hint="Blank = never.">
          <input
            className="input"
            inputMode="numeric"
            value={draft.autoResolveAfterMinutes}
            onChange={(e) => set('autoResolveAfterMinutes', e.target.value)}
          />
        </Field>
        <Field label="Runs / conversation / day">
          <input
            className="input"
            inputMode="numeric"
            value={draft.maxRunsPerConversationDay}
            onChange={(e) => set('maxRunsPerConversationDay', e.target.value)}
          />
        </Field>
        <Field label="Runs / day (all)">
          <input
            className="input"
            inputMode="numeric"
            value={draft.maxRunsPerTenantDay}
            onChange={(e) => set('maxRunsPerTenantDay', e.target.value)}
          />
        </Field>
      </div>

    </section>
    <SaveBar
      dirty={dirty}
      busy={busy}
      saved={saved}
      blocked={!draft.name.trim()}
      savedNote="Assistant saved."
      onSave={() => void save()}
      onDiscard={() => {
        setDraft(draftFrom(base));
        setError(null);
      }}
    />
    </>
  );
}

